<template>
    <div>

      <BaseButton
        :tabindex="$attrs.tabindex"
        :label="label"
        :disabled="disabled"
        @click="_openDialog"
        class="mt-5">
      </BaseButton>

      <v-dialog max-width="450px"
        :value="openDialog" 
        @keydown.esc="_closeDialog" 
        @click:outside="_closeDialog">

        <v-card
          :class="{ 'grey lighten-2': dragover }"
          @drop.prevent="_onDrop($event)"
          @dragover.prevent="dragover = true"
          @dragenter.prevent="dragover = true"
          @dragleave.prevent="dragover = false">
         
          <v-toolbar dark color="primary">
            <v-btn icon dark
             @click="_closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>

            <v-toolbar-title><div v-html="$let('DocPackUploadSectionTitle')"></div></v-toolbar-title>

          </v-toolbar>

          <v-card-text>
            <v-row dense class="d-flex flex-column row-pointer uploadText" align="center" justify="center" @click="_openFileBrowse">
              <v-icon :class="[dragover ? 'mt-2, mb-6' : 'mt-5']" size="60">mdi-cloud-upload</v-icon>
              <v-sheet class=''>{{ entryLabel }}</v-sheet>
              <v-sheet :class="allowMultipleClass">{{ allowedMultipleText }}</v-sheet> 
              <v-sheet :class="allowExtensionsClass" v-html="compAllowedExtensionsText"></v-sheet>
            </v-row>

            <v-virtual-scroll height="120" item-height="50" class="mt-8"
              v-if="selectedFiles.length > 0"
              :items="selectedFiles">

              <template v-slot:default="{ item }">
                <v-list-item :key="item.name">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}<span class="ml-3 text--secondary">{{ item.size }} bytes</span></v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn icon 
                      @click.stop="_removeFile(item.name)" >
                        <v-icon> mdi-close-circle </v-icon>
                    </v-btn>
                  </v-list-item-action>
                  
                </v-list-item>
                <v-divider></v-divider>
              </template>

            </v-virtual-scroll>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn icon light rounded fab
            color="primary"
              @click.stop="_uploadFiles">
                <v-icon id="upload-button">mdi-upload</v-icon>
            </v-btn>
          </v-card-actions>

        </v-card>

        <v-file-input :multiple="multiple" v-show="false"
          v-model="browsedFiles"
          @change="_filesSelected($event)"
          ref="inputFile">
        </v-file-input>

      </v-dialog>

    </div>
</template>

<script>

  import BaseButton from '@/shared/components/base-button.vue';
  import textKeys from '../constants/display-texts.js';

  export default {
    name: "FileUpload",
    inheritAttrs: false,
    components: {
      BaseButton
    },
    emits: ["uploadFiles"],
    props: {
      label: { type: String, required: true },
      allowedExtensionsText: { type: String, required: false },
      allowedExtensions: { type: String },
      allowedMultipleText: { type: String, required: false },
      entryLabel: { type: String, required: false },
      multiple: { type: Boolean, default: false },
      carryItem: { type: Object },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return {
        textKeys: textKeys,
        openDialog: false,
        dragover: false,
        allowMultipleClass: '',
        allowExtensionsClass: '',
        selectedFiles: [],
        browsedFiles:[],
        startFiles: []
      };
    },
    
    methods: {
      _uploadFiles() {
        if (this.selectedFiles!== []) {
          this.$emit("uploadFiles", this.carryItem, this.selectedFiles);
          this.openDialog = false;
        }
      },

      _openDialog() {
        this.allowMultipleClass = '';
        this.allowExtensionsClass = '';
        this.startFiles = this.selectedFiles; // Store previous selection to allow reset on cancel
        this.openDialog = true; 
      },

      _closeDialog() {
        this.selectedFiles = this.startFiles; // Reset on cancel action
        this.openDialog = false; 
      },

      _openFileBrowse() { 
        this.$refs.inputFile.$refs.input.click(); 
      },
      
      _removeFile(fileName) {
        const index = this.selectedFiles.findIndex(file => file.name === fileName);
        if (index > -1) this.selectedFiles.splice(index, 1);
      },

      _filesSelected(files) {
        if (!this.multiple) this.selectedFiles = [];
        this.allowExtensionsClass = '';
        if (files) {
          const arrFiles = Array.isArray(files) ? files : [files];
          arrFiles.every(file => this.selectFile(file));
        }
      },

      _onDrop(evt) {
        this.dragover = false;
        this.allowMultipleClass = '';
        if (!this.multiple && evt.dataTransfer.files.length > 1) this.allowMultipleClass = 'red-text';
        else {
          if (!this.multiple) this.selectedFiles = [];
          Array.from(evt.dataTransfer.files).every(file => this.selectFile(file));
        }
      },

      allowedExtension(file) {
        const fileExtension = /[.]/.exec(file.name) ? /[^.]+$/.exec(file.name) : undefined;
        if (!fileExtension) return false;
        const arrAllowed = this.allowedExtensions.split(",");
        return arrAllowed.filter(e => e.toLowerCase().replaceAll(" ", "") === fileExtension[0].toLowerCase()).length > 0;
      }, 

      selectFile(file) {
        if (this.allowedExtension(file)) { 
          this.selectedFiles.push(file); 
          return true;
        }
        else {
          this.selectedFiles = this.startFiles; // Reset files on error
          this.allowExtensionsClass = 'red-text';
          return false;
        }
      }
    },

    computed: {
      compAllowedExtensionsText() {
        return this.allowedExtensionsText.replace("{AllowedFileExtensions}", this.allowedExtensions);
      }
    }
  };
</script>

<style lang="css" scoped>
  .row-pointer :hover {
    cursor: pointer;
  }

  .red-text {
    color: red;
  }

  .uploadText {
    text-align: center;
  }
</style>